import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Typography, TextField, MenuItem, Button, Checkbox, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { addWorkItemPartsBulk } from '../../actions/apiWorkItemParts';
import {
 AVAILABILITY, num, computeTotal, money,
} from './partsUtils';

const emptyNewDraft = {
  part_number: '', description: '', price: '', quantity: 1, availability: 'In Stock',
};

const AddPartsModal = ({
  open, onClose, catalogOptions, basePayload, vehicleId,
}) => {
  const dispatch = useDispatch();
  const [tab, setTab] = useState('catalog');
  const [step, setStep] = useState('select');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [config, setConfig] = useState({});
  const [newDraft, setNewDraft] = useState(emptyNewDraft);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setTab('catalog');
    setStep('select');
    setSearch('');
    setSelected({});
    setConfig({});
    setNewDraft(emptyNewDraft);
    setSaving(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const selectedIds = useMemo(() => Object.keys(selected).filter(id => selected[id]), [selected]);

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    const matchesSearch = p => !s || `${p.part_number} ${p.description || ''}`.toLowerCase().includes(s);
    const results = catalogOptions.filter(matchesSearch);
    const pinned = catalogOptions.filter(p => selected[p.id] && !matchesSearch(p));
    return { pinned, results };
  }, [catalogOptions, search, selected]);

  const toggle = id => setSelected(prev => ({ ...prev, [id]: !prev[id] }));

  const goConfigure = () => {
    setConfig((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => { if (!next[id]) next[id] = { quantity: 1, availability: 'In Stock' }; });
      return next;
    });
    setStep('configure');
  };

  const setLine = (id, field, value) => setConfig(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const buildItem = (part, cfg) => {
    const price = num(part.price);
    const quantity = num(cfg.quantity) || 1;
    return {
      ...basePayload,
      catalog_part_id: part.catalog_part_id ?? part.id ?? null,
      part_number: String(part.part_number || '').trim(),
      description: String(part.description || '').trim(),
      price,
      quantity,
      availability: cfg.availability || 'In Stock',
      total: computeTotal(price, quantity),
    };
  };

  const addCatalogParts = async () => {
    const items = selectedIds
      .map(id => catalogOptions.find(p => String(p.id) === String(id)))
      .filter(Boolean)
      .map(part => buildItem(part, config[part.id] || { quantity: 1, availability: 'In Stock' }));
    if (items.length === 0) return;
    setSaving(true);
    try {
      await dispatch(addWorkItemPartsBulk(items, vehicleId));
      close();
    } finally {
      setSaving(false);
    }
  };

  const saveNewPart = async () => {
    if (!newDraft.part_number.trim()) return;
    const item = buildItem(
      {
 catalog_part_id: null, part_number: newDraft.part_number, description: newDraft.description, price: newDraft.price,
},
      { quantity: newDraft.quantity, availability: newDraft.availability },
    );
    setSaving(true);
    try {
      await dispatch(addWorkItemPartsBulk([item], vehicleId));
      close();
    } finally {
      setSaving(false);
    }
  };

  const setNew = (field, value) => setNewDraft(prev => ({ ...prev, [field]: value }));

  const priceCell = p => (p.price === null || p.price === undefined ? 'No price' : `$${money(p.price)}`);

  const renderSelectRow = (p, pinned) => (
    <TableRow key={p.id} hover selected={!!selected[p.id]} sx={pinned ? { backgroundColor: '#eef4fa' } : {}}>
      <TableCell padding="checkbox">
        <Checkbox size="small" checked={!!selected[p.id]} onChange={() => toggle(p.id)} />
      </TableCell>
      <TableCell>{p.part_number}</TableCell>
      <TableCell>{p.description}</TableCell>
      <TableCell align="right">{priceCell(p)}</TableCell>
    </TableRow>
  );

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Add Parts to Work Item</DialogTitle>
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          borderBottom: '1px solid #e0e0e0',
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': {
            fontWeight: 600,
            color: 'text.secondary',
            backgroundColor: '#f5f7fa',
            '&.Mui-selected': { backgroundColor: 'primary.main', color: 'primary.contrastText' },
          },
        }}
      >
        <Tab value="catalog" label="Select from Catalog" />
        <Tab value="new" label="Add New Part" />
      </Tabs>

      <DialogContent dividers>
        {tab === 'catalog' && step === 'select' && (
          <>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Search the catalog"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onFocus={e => e.target.select()}
              sx={{ mb: 2 }}
            />
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Number</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.pinned.map(p => renderSelectRow(p, true))}
                {rows.pinned.length > 0 && rows.results.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ p: 0 }}><Divider /></TableCell>
                  </TableRow>
                )}
                {rows.results.map(p => renderSelectRow(p, false))}
                {rows.pinned.length === 0 && rows.results.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="textSecondary">No matching parts.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}

        {tab === 'catalog' && step === 'configure' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {selectedIds
              .map(id => catalogOptions.find(p => String(p.id) === String(id)))
              .filter(Boolean)
              .map((p) => {
                const cfg = config[p.id] || { quantity: 1, availability: 'In Stock' };
                return (
                  <Box
                    key={p.id}
                    sx={{
                      display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', p: 1.5, bgcolor: '#eef4fa', borderRadius: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.description}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {p.part_number}
                        {' · '}
                        {priceCell(p)}
                        {' per unit'}
                      </Typography>
                    </Box>
                    <TextField
                      size="small"
                      type="number"
                      label="Quantity Used"
                      sx={{ width: 130 }}
                      value={cfg.quantity}
                      onChange={e => setLine(p.id, 'quantity', e.target.value)}
                    />
                    <TextField
                      size="small"
                      select
                      label="Availability"
                      sx={{ width: 150 }}
                      value={cfg.availability}
                      onChange={e => setLine(p.id, 'availability', e.target.value)}
                    >
                      {AVAILABILITY.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                    </TextField>
                  </Box>
                );
              })}
          </Box>
        )}

        {tab === 'new' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              size="small"
              required
              label="Part Number"
              helperText="Enter a unique identifier for this part"
              value={newDraft.part_number}
              onChange={e => setNew('part_number', e.target.value)}
            />
            <TextField
              size="small"
              required
              label="Part Description"
              helperText="Enter a short description of the part"
              value={newDraft.description}
              onChange={e => setNew('description', e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                type="number"
                label="Part Price"
                helperText="Cost per unit"
                sx={{ width: 150 }}
                value={newDraft.price}
                onChange={e => setNew('price', e.target.value)}
              />
              <TextField
                size="small"
                type="number"
                label="Quantity Used"
                helperText="Units used here"
                sx={{ width: 150 }}
                value={newDraft.quantity}
                onChange={e => setNew('quantity', e.target.value)}
              />
              <TextField
                size="small"
                select
                label="Availability"
                sx={{ width: 160 }}
                value={newDraft.availability}
                onChange={e => setNew('availability', e.target.value)}
              >
                {AVAILABILITY.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </TextField>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {tab === 'catalog' && step === 'configure' && (
          <Button variant="text" onClick={() => setStep('select')} sx={{ mr: 'auto' }}>Back</Button>
        )}
        <Button variant="outlined" onClick={close}>Cancel</Button>
        {tab === 'catalog' && step === 'select' && (
          <Button variant="contained" disabled={selectedIds.length === 0} onClick={goConfigure}>
            Next
          </Button>
        )}
        {tab === 'catalog' && step === 'configure' && (
          <Button variant="contained" disabled={saving || selectedIds.length === 0} onClick={addCatalogParts}>
            {saving ? 'Adding...' : 'Add Parts to Work Item'}
          </Button>
        )}
        {tab === 'new' && (
          <Button
            variant="contained"
            disabled={saving || !newDraft.part_number.trim() || !newDraft.description.trim()}
            onClick={saveNewPart}
          >
            {saving ? 'Saving...' : 'Save and Add Part'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddPartsModal;
